class BundleBuilder extends HTMLElement {
  constructor() {
    super();
    this.step = parseInt(this.dataset.step, 10) || 2;
    this.cap = parseInt(this.dataset.cap, 10) || 6;
    this.noun = this.dataset.noun || 'sleeve';
    this.nounPlural = this.dataset.nounPlural || this.noun + 's';
    this.ctaSub = this.dataset.ctaSub || '';
    this.claimNote = this.dataset.claimNote || '';
    this.bulkText = this.dataset.bulkText || '';
    this.bulkUrl = this.dataset.bulkUrl || '';
    this.items = this.parseItems(this.dataset.items);
    this.tiers = this.parseLadder(this.dataset.ladder);
    this.unitsPerPack = parseInt(this.dataset.unitsPerPack, 10) || 1;
    this.freeShipFrom = parseInt(this.dataset.freeShipFrom, 10) || 0;
    // Below this fraction of one unit's price, the next step is cheap enough
    // that the dollar amount sells harder than the per-unit discount.
    this.stealRatio = (parseInt(this.dataset.stealPercent, 10) || 50) / 100;
    this.unitItem = this.dataset.unitItem || '';
    this.unitItemPlural = this.dataset.unitItemPlural || this.unitItem;
    this.rows = this.querySelector('[data-rows]');
    this.ladderEl = this.querySelector('[data-ladder-ui]');
    this.unlock = this.querySelector('[data-unlock]');
    this.money = this.querySelector('[data-money]');
    this.addButton = this.querySelector('[data-add]');
    this.ctaSubEl = this.querySelector('[data-cta-sub]');
  }

  get ladderMode() {
    return this.tiers.length > 0;
  }

  connectedCallback() {
    if (!this.items.length) return;
    this.renderRows();
    if (this.ladderMode) {
      this.renderLadder();
      this.warnOnPriceMismatch();
    }
    this.addButton.addEventListener('click', this.onAdd.bind(this));
    if (this.dataset.sticky === 'true') this.buildSticky();
    this.draw();
  }

  // "1|14.99" per line. Last tier is open-ended ("4+"). Empty = legacy free-unit mode.
  parseLadder(raw) {
    if (!raw) return [];
    return raw
      .split(/[\n,]/)
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const [qty, price] = line.split('|');
        return { qty: parseInt(qty, 10), price: Math.round(parseFloat(price) * 100) };
      })
      .filter((t) => t.qty > 0 && t.price >= 0)
      .sort((a, b) => a.qty - b.qty);
  }

  tierIndexFor(n) {
    let idx = 0;
    this.tiers.forEach((t, i) => {
      if (n >= t.qty) idx = i;
    });
    return idx;
  }

  renderLadder() {
    const last = this.tiers.length - 1;
    this.ladderEl.hidden = false;
    this.ladderEl.innerHTML =
      (this.dataset.ladderTitle || this.dataset.ladderBadge
        ? '<p class="bundle-ladder__title">' +
          '<span>' +
          (this.dataset.ladderTitle || '') +
          '</span>' +
          (this.dataset.ladderBadge
            ? '<span class="bundle-ladder__save">' + this.dataset.ladderBadge + '</span>'
            : '') +
          '</p>'
        : '') +
      '<div class="bundle-ladder__box">' +
      (this.dataset.bestLabel
        ? '<span class="bundle-ladder__flag">' + this.dataset.bestLabel + '</span>'
        : '') +
      '<div class="bundle-ladder__grid" style="--tiers:' +
      this.tiers.length +
      '">' +
      '<div class="bundle-ladder__row bundle-ladder__row--qty">' +
      this.tiers
        .map(
          (t, i) =>
            '<span class="bundle-ladder__cell bundle-ladder__qty" data-tier="' +
            i +
            '">' +
            t.qty +
            (i === last ? '+' : '') +
            ' ' +
            this.plural(i === last ? 2 : t.qty) +
            '</span>'
        )
        .join('') +
      '</div>' +
      '<div class="bundle-ladder__row bundle-ladder__row--dots">' +
      '<span class="bundle-ladder__track"><i data-ladder-fill style="width:0%"></i></span>' +
      this.tiers
        .map(
          (t, i) =>
            '<span class="bundle-ladder__cell" data-tier="' +
            i +
            '"><span class="bundle-ladder__dot" aria-hidden="true"></span></span>'
        )
        .join('') +
      '</div>' +
      '<div class="bundle-ladder__row bundle-ladder__row--price">' +
      this.tiers
        .map(
          (t, i) =>
            '<span class="bundle-ladder__cell bundle-ladder__price" data-tier="' +
            i +
            '">' +
            this.money_(t.price) +
            '/ea</span>'
        )
        .join('') +
      '</div>' +
      (this.dataset.showTierPercent === 'true'
        ? '<div class="bundle-ladder__row bundle-ladder__row--off">' +
          this.tiers
            .map(
              (t, i) =>
                '<span class="bundle-ladder__cell bundle-ladder__off" data-tier="' +
                i +
                '">' +
                this.percentOffLabel(t.price) +
                '</span>'
            )
            .join('') +
          '</div>'
        : '') +
      '</div>' +
      (this.freeShipFrom > 0
        ? '<p class="bundle-ladder__ship" data-ship-line><span class="bundle-ladder__ship-tick" aria-hidden="true">&check;</span>Free shipping on ' +
          this.freeShipFrom +
          '+ ' +
          this.nounPlural +
          '</p>'
        : '') +
      '</div></div>';
  }

  // Live Shopify price. Drives the strikethrough and savings, which have to
  // reflect what the cart will actually charge.
  listCents() {
    if (!this.items.length) return 0;
    return Math.round(this.items.reduce((s, it) => s + it.price, 0) / this.items.length);
  }

  // Percentages are relative to the ladder's own first tier, which is the
  // number printed directly above them. Using the live product price here
  // would label "$19.99 -> $11.99" as 20% off whenever the ladder is
  // configured ahead of a price change in admin.
  percentOffLabel(tierPrice) {
    const base = this.tiers.length ? this.tiers[0].price : 0;
    if (!base || tierPrice >= base) return '';
    return Math.round(((base - tierPrice) / base) * 100) + '% off';
  }

  // The ladder's first tier is a promise about the list price. If admin
  // disagrees, every price on this page is wrong — say so in the console
  // rather than rendering a confident number that the cart will contradict.
  warnOnPriceMismatch() {
    if (!this.tiers.length) return;
    const live = this.listCents();
    if (live && live !== this.tiers[0].price) {
      console.warn(
        '[bundle-builder] ladder tier 1 is ' +
          this.money_(this.tiers[0].price) +
          ' but the live product price is ' +
          this.money_(live) +
          '. The cart will charge the live price. Update the ladder or the product price before publishing.'
      );
    }
  }

  drawLadder(n) {
    const active = this.tierIndexFor(n);
    const last = this.tiers.length - 1;
    this.ladderEl.querySelectorAll('[data-tier]').forEach((el) => {
      const i = parseInt(el.dataset.tier, 10);
      el.classList.toggle('is-reached', n >= this.tiers[i].qty);
      el.classList.toggle('is-current', n > 0 && i === active);
    });

    let pct = 0;
    if (n > 0 && last > 0) {
      if (active === last) {
        pct = 100;
      } else {
        const span = this.tiers[active + 1].qty - this.tiers[active].qty;
        const frac = span > 0 ? (n - this.tiers[active].qty) / span : 0;
        pct = ((active + frac) / last) * 100;
      }
    }
    this.ladderEl.querySelector('[data-ladder-fill]').style.width = pct + '%';

    const shipLine = this.ladderEl.querySelector('[data-ship-line]');
    if (shipLine) shipLine.classList.toggle('is-active', n >= this.freeShipFrom);
  }

  buildSticky() {
    this.sticky = document.createElement('div');
    this.sticky.className = 'bundle-sticky';
    this.sticky.setAttribute('aria-hidden', 'true');
    this.sticky.innerHTML =
      '<div class="bundle-sticky__inner">' +
      '<div class="bundle-sticky__info">' +
      '<span class="bundle-sticky__prices"><strong data-sticky-total></strong>' +
      '<s data-sticky-was></s></span>' +
      '<span class="bundle-sticky__save" data-sticky-note></span></div>' +
      '<button type="button" class="button bundle-sticky__cta" data-sticky-add>' +
      (this.dataset.stickyLabel || 'BUILD YOUR ORDER') +
      '</button></div>';
    document.body.appendChild(this.sticky);
    this.sticky.querySelector('[data-sticky-add]').addEventListener('click', () => {
      this.scrollIntoView({ behavior: 'smooth', block: 'center' });
      this.flashRows();
    });

    this.syncSticky = () => {
      if (this.stickyTick) return;
      this.stickyTick = requestAnimationFrame(() => {
        this.stickyTick = null;
        const scrolledPast = this.addButton.getBoundingClientRect().bottom < 0;
        this.sticky.classList.toggle('is-visible', scrolledPast);
        this.sticky.setAttribute('aria-hidden', scrolledPast ? 'false' : 'true');
      });
    };

    window.addEventListener('scroll', this.syncSticky, { passive: true });
    window.addEventListener('resize', this.syncSticky, { passive: true });
    this.syncSticky();
  }

  disconnectedCallback() {
    if (this.syncSticky) {
      window.removeEventListener('scroll', this.syncSticky);
      window.removeEventListener('resize', this.syncSticky);
    }
    if (this.stickyTick) cancelAnimationFrame(this.stickyTick);
    if (this.sticky) this.sticky.remove();
  }

  parseItems(raw) {
    if (!raw) return [];
    return raw
      .split('~~')
      .filter(Boolean)
      .map((chunk) => {
        const [id, label, swatch, price, qty, image, sublabel] = chunk.split('|');
        return {
          id: id,
          label: label,
          swatch: swatch || '#ffffff',
          price: parseInt(price, 10) || 0,
          qty: parseInt(qty, 10) || 0,
          image: image || '',
          sublabel: sublabel || '',
        };
      });
  }

  renderRows() {
    this.rows.innerHTML = this.items
      .map(
        (it, i) =>
          '<div class="bundle-row">' +
          (it.image
            ? '<img class="bundle-row__thumb" src="' +
              it.image +
              '" alt="" width="56" height="56" loading="lazy">'
            : '<span class="bundle-row__swatch" style="background:' + it.swatch + '"></span>') +
          '<span class="bundle-row__name">' +
          it.label +
          (it.sublabel ? '<span class="bundle-row__sub">' + it.sublabel + '</span>' : '') +
          '</span>' +
          '<span class="bundle-row__step">' +
          '<button type="button" data-i="' +
          i +
          '" data-d="-1" aria-label="Remove one ' +
          it.label +
          '">&minus;</button>' +
          '<span data-count="' +
          i +
          '" aria-live="polite">' +
          it.qty +
          '</span>' +
          '<button type="button" data-i="' +
          i +
          '" data-d="1" aria-label="Add one ' +
          it.label +
          '">+</button>' +
          '</span></div>'
      )
      .join('');

    this.rows.querySelectorAll('button').forEach((btn) => {
      btn.addEventListener('click', () => {
        const i = parseInt(btn.dataset.i, 10);
        const d = parseInt(btn.dataset.d, 10);
        this.items[i].qty = Math.max(0, this.items[i].qty + d);
        this.draw();
      });
    });
  }

  get paidCount() {
    return this.items.reduce((sum, it) => sum + it.qty, 0);
  }

  get paidCents() {
    return this.items.reduce((sum, it) => sum + it.qty * it.price, 0);
  }

  freeCount(n) {
    return Math.min(Math.floor(n / this.step) * this.step, this.cap);
  }

  unitPriceCents() {
    const withQty = this.items.filter((it) => it.qty > 0);
    const pool = withQty.length ? withQty : this.items;
    return Math.round(pool.reduce((s, it) => s + it.price, 0) / pool.length);
  }

  money_(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  plural(n) {
    return n === 1 ? this.noun : this.nounPlural;
  }

  draw() {
    const n = this.paidCount;

    this.items.forEach((it, i) => {
      this.rows.querySelector('[data-count="' + i + '"]').textContent = it.qty;
      this.rows.querySelector('button[data-i="' + i + '"][data-d="-1"]').disabled = it.qty === 0;
    });

    if (this.ladderMode) return this.drawLadderMode(n);

    const free = this.freeCount(n);
    this.unlock.innerHTML = free === 0 ? this.nudgeMarkup(n) : this.wonMarkup(n, free);
    this.money.innerHTML = this.moneyMarkup(n, free);

    this.addButton.disabled = n === 0;
    if (this.ctaSubEl) {
      this.ctaSubEl.textContent = free > 0 && this.ctaSub ? this.ctaSub.replace('[n]', free) : '';
    }

    if (this.sticky) {
      const save = free * this.unitPriceCents();
      this.sticky.querySelector('[data-sticky-total]').textContent = this.money_(this.paidCents);
      this.sticky.querySelector('[data-sticky-was]').textContent =
        save > 0 ? this.money_(this.paidCents + save) : '';
      this.sticky.querySelector('[data-sticky-note]').textContent =
        save > 0 ? 'You save ' + this.money_(save) : '';
    }
  }

  drawLadderMode(n) {
    this.drawLadder(n);

    const tier = this.tiers[this.tierIndexFor(n)];
    const pay = n * tier.price;
    const full = this.paidCents;
    const save = Math.max(0, full - pay);

    this.unlock.innerHTML = this.ladderNudgeMarkup(n);
    this.money.innerHTML = this.ladderMoneyMarkup(n, tier, pay, full, save);

    this.addButton.disabled = n === 0;
    if (this.ctaSubEl) this.ctaSubEl.textContent = '';

    if (this.sticky) {
      this.sticky.querySelector('[data-sticky-total]').textContent = this.money_(pay);
      this.sticky.querySelector('[data-sticky-was]').textContent = save > 0 ? this.money_(full) : '';
      this.sticky.querySelector('[data-sticky-note]').textContent =
        save > 0 ? 'You save ' + this.money_(save) : '';
    }
  }

  ladderNudgeMarkup(n) {
    const next = this.tiers.find((t) => t.qty > n);
    if (!next) return '';
    const current = this.tiers[this.tierIndexFor(n)];
    const need = next.qty - n;
    const drop = current.price - next.price;
    if (drop <= 0) return '';

    // Only mention shipping when this very step is the one that unlocks it.
    const unlocksShipping =
      this.freeShipFrom > 0 && n < this.freeShipFrom && next.qty >= this.freeShipFrom;

    // What the shopper actually pays to take this step. Deeper tiers reprice
    // the units already in the cart, so near the top of the ladder this lands
    // far below the sticker price of one unit — worth leading with.
    const marginal = next.qty * next.price - n * current.price;
    const isSteal = n > 0 && marginal > 0 && marginal <= current.price * this.stealRatio;

    const label = isSteal
      ? 'Add ' + need + ' more ' + this.plural(need) + ' for just <strong>' + this.money_(marginal) + '</strong>'
      : 'Add ' +
        need +
        (n > 0 ? ' more ' : ' ') +
        this.plural(need) +
        ' to drop to ' +
        this.money_(next.price) +
        ' each' +
        (unlocksShipping ? ' + free shipping' : '');

    return (
      '<div class="bundle-nudge' +
      (isSteal ? ' bundle-nudge--steal' : '') +
      '"><p><span class="bundle-nudge__plus">+</span>' +
      label +
      '</p></div>'
    );
  }

  perItemMarkup(tierPrice) {
    if (this.unitsPerPack < 2 || !this.unitItem) return '';
    const each = tierPrice / this.unitsPerPack;
    return (
      '<div class="bundle-money__row bundle-money__row--unit"><span>Works out to</span><span>' +
      this.money_(each) +
      ' per ' +
      this.unitItem +
      '</span></div>'
    );
  }

  ladderMoneyMarkup(n, tier, pay, full, save) {
    let html =
      '<div class="bundle-money__row bundle-money__row--total"><span>You pay</span><strong>' +
      this.money_(pay) +
      '</strong></div>';
    if (save > 0) {
      html +=
        '<div class="bundle-money__row"><span>Full price</span><s>' +
        this.money_(full) +
        '</s></div>' +
        '<div class="bundle-money__row bundle-money__row--save"><span>You save</span><span>' +
        this.money_(save) +
        '</span></div>';
    }
    if (n > 0) html += this.perItemMarkup(tier.price);
    return html;
  }

  flashRows() {
    this.rows.classList.remove('bundle-flash');
    void this.rows.offsetWidth;
    this.rows.classList.add('bundle-flash');
  }

  nudgeMarkup(n) {
    const need = this.step - n;
    const pct = Math.min(100, (n / this.step) * 100);
    return (
      '<div class="bundle-nudge"><p><span class="bundle-nudge__plus">+</span>Add ' +
      need +
      (n > 0 ? ' more ' : ' ') +
      this.plural(need) +
      ' to unlock ' +
      this.step +
      ' free and free shipping</p>' +
      '<span class="bundle-bar"><i style="width:' +
      pct +
      '%"></i></span></div>'
    );
  }

  wonMarkup(n, free) {
    let html =
      '<div class="bundle-won"><ul>' +
      '<li><span class="bundle-tick">&check;</span>' +
      free +
      ' free ' +
      this.plural(free) +
      ' unlocked</li>' +
      '<li><span class="bundle-tick">&check;</span>Free shipping</li></ul>';
    if (this.claimNote) {
      html += '<p class="bundle-claim">' + this.claimNote + '</p>';
    }
    return html + '</div>';
  }

  moneyMarkup(n, free) {
    const pay = this.paidCents;
    const value = pay + free * this.unitPriceCents();
    const save = value - pay;
    let html =
      '<div class="bundle-money__row bundle-money__row--total"><span>You pay</span><strong>' +
      this.money_(pay) +
      '</strong></div>';
    if (free > 0) {
      html +=
        '<div class="bundle-money__row"><span>Full value</span><s>' +
        this.money_(value) +
        '</s></div>' +
        '<div class="bundle-money__row bundle-money__row--save"><span>You save</span><span>' +
        this.money_(save) +
        '</span></div>';
    }
    return html;
  }

  async onAdd() {
    const items = this.items
      .filter((it) => it.qty > 0)
      .map((it) => ({ id: it.id, quantity: it.qty }));
    if (!items.length) return;

    const label = this.querySelector('[data-cta-label]');
    const original = label.textContent;
    label.textContent = 'ADDING…';
    this.addButton.disabled = true;

    const drawer = document.querySelector('cart-drawer');
    const body = { items };
    if (drawer && typeof drawer.renderContents === 'function') {
      body.sections = drawer.getSectionsToRender().map((s) => s.id);
      body.sections_url = window.location.pathname;
      if (typeof drawer.setActiveElement === 'function') {
        drawer.setActiveElement(document.activeElement);
      }
    }

    try {
      const response = await fetch(this.dataset.addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.description || err.message || 'Cart returned ' + response.status);
      }
      const parsed = await response.json();

      if (drawer && typeof drawer.renderContents === 'function' && parsed.sections) {
        if (drawer.classList.contains('is-empty')) drawer.classList.remove('is-empty');
        drawer.renderContents(parsed);
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        if (window.PUB_SUB_EVENTS && window.publish) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'bundle-builder' });
        }
        label.textContent = original;
        this.addButton.disabled = false;
        return;
      }

      window.location = this.dataset.cartUrl;
    } catch (error) {
      console.error('[bundle-builder] add to cart failed', error);
      label.textContent = original;
      this.addButton.disabled = false;
      this.showError(error.message);
    }
  }

  showError(message) {
    let box = this.querySelector('.bundle-error');
    if (!box) {
      box = document.createElement('p');
      box.className = 'bundle-error';
      this.insertBefore(box, this.addButton.nextSibling);
    }
    box.textContent = "Sorry, that couldn't be added to your cart. " + (message || '');
  }
}

customElements.define('bundle-builder', BundleBuilder);

function refreshShipByDates() {
  document.querySelectorAll('[data-ship-by]').forEach((el) => {
    const days = parseInt(el.dataset.days, 10);
    if (Number.isNaN(days)) return;
    const date = new Date();
    date.setDate(date.getDate() + days);
    if (el.dataset.skipWeekends === 'true') {
      if (date.getDay() === 6) date.setDate(date.getDate() + 2);
      if (date.getDay() === 0) date.setDate(date.getDate() + 1);
    }
    const fresh = date
      .toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      .toUpperCase();
    if (fresh !== el.textContent.trim()) el.textContent = fresh;
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', refreshShipByDates);
} else {
  refreshShipByDates();
}
